import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, MessagesSquare, Bell, Sparkles, LogOut, User as UserIcon, Settings } from "lucide-react";
import Logo from "@/components/Logo";
import ThemeSwitch from "@/components/ThemeSwitch";
import SparkPill from "@/components/app/SparkPill";
import GlobalSearch from "@/components/app/GlobalSearch";
import { useAuth } from "@/context/AuthContext";
import { currentUser as guestUser } from "@/lib/mockData";

export default function TopBar() {
  const auth = useAuth();
  const me = auth?.user;
  const isAuthed = me && typeof me === "object";
  const display = isAuthed ? me : guestUser;
  const avatar = isAuthed
    ? (me.avatar || `https://api.dicebear.com/7.x/notionists/svg?seed=${encodeURIComponent(me.email)}`)
    : guestUser.avatar;

  return (
    <header
      data-testid="topbar"
      className="sticky top-0 z-40 h-16 bg-cream/85 backdrop-blur-xl border-b hairline"
    >
      <div className="h-full max-w-[1600px] mx-auto px-4 md:px-6 flex items-center gap-4">
        <Link to="/"><Logo size={36} asLink={false} /></Link>

        <GlobalSearch />

        <div className="ml-auto flex items-center gap-1.5 md:gap-2">
          <button
            data-testid="topbar-create"
            className="hidden md:inline-flex items-center gap-2 bg-ink text-cream px-4 py-2 rounded-full text-sm hover:bg-coral transition-colors"
          >
            <Plus className="w-4 h-4" /> Crear
          </button>
          <IconBtn testid="topbar-ai"      icon={Sparkles}        badge="IA" />
          <IconBtn testid="topbar-msg"     icon={MessagesSquare}  badge={5}  to="/mensajes" />
          <IconBtn testid="topbar-notif"   icon={Bell}            badge={12} to="/notificaciones" />
          <SparkPill />
          <ThemeSwitch />
          {isAuthed ? (
            <UserMenu me={display} avatar={avatar} />
          ) : (
            <Link
              to="/login"
              data-testid="topbar-login"
              className="ml-1 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-coral text-white text-sm hover:bg-coral-hover transition"
            >
              Entrar
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}

function UserMenu({ me, avatar }) {
  const auth = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <div ref={ref} className="relative ml-1">
      <button
        data-testid="topbar-avatar"
        onClick={() => setOpen((v) => !v)}
        className="shrink-0"
      >
        <img src={avatar} alt={me.name} className="w-9 h-9 rounded-full ring-2 ring-coral/30 hover:ring-coral transition bg-cream-deep" />
      </button>
      {open && (
        <div data-testid="user-menu" className="absolute right-0 mt-2 w-60 bg-cream-card border hairline rounded-2xl shadow-xl overflow-hidden z-50">
          <div className="px-4 py-3 border-b hairline">
            <p className="text-sm font-medium truncate">{me.name}</p>
            <p className="text-xs text-ink-mid truncate font-mono">{me.email || me.handle}</p>
          </div>
          <MenuItem testid="user-menu-profile" icon={UserIcon} label="Mi perfil" onClick={() => { setOpen(false); nav("/perfil"); }} />
          <MenuItem testid="user-menu-settings" icon={Settings} label="Ajustes" />
          <MenuItem
            testid="user-menu-logout"
            icon={LogOut}
            label="Cerrar sesión"
            onClick={() => { auth.logout(); setOpen(false); nav("/"); }}
            danger
          />
        </div>
      )}
    </div>
  );
}

function MenuItem({ icon: Icon, label, onClick, testid, danger }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm transition text-left ${danger ? "text-red-700 hover:bg-red-50" : "text-ink hover:bg-cream-deep"}`}
    >
      <Icon className="w-4 h-4" strokeWidth={1.7} /> {label}
    </button>
  );
}

function IconBtn({ icon: Icon, badge, testid, to }) {
  const cls = "relative w-10 h-10 rounded-full bg-white/70 border hairline hover:border-coral/60 hover:bg-white transition flex items-center justify-center text-ink";
  const content = (
    <>
      <Icon className="w-[18px] h-[18px]" strokeWidth={1.7} />
      {badge !== undefined && (
        <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-coral text-cream text-[10px] font-mono inline-flex items-center justify-center">
          {badge}
        </span>
      )}
    </>
  );
  if (to) return <Link to={to} data-testid={testid} className={cls}>{content}</Link>;
  return <button data-testid={testid} className={cls}>{content}</button>;
}
